
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface LoggingStats {
  totalLogs: number;
  logsToday: number;
  uniqueServices: string[];
  lastLogTime: string | null;
}

export const LoggingStatus: React.FC = () => {
  const [stats, setStats] = useState<LoggingStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoggingStats();
  }, []);

  const fetchLoggingStats = async () => {
    try {
      // Få totalt antall logger
      const { count: totalCount } = await supabase
        .from('api_call_logs')
        .select('*', { count: 'exact', head: true });

      // Få dagens logger
      const today = new Date().toISOString().split('T')[0];
      const { count: todayCount } = await supabase
        .from('api_call_logs')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today);

      // Få unike tjenester
      const { data: servicesData } = await supabase
        .from('api_call_logs')
        .select('api_service')
        .order('created_at', { ascending: false })
        .limit(100);

      const uniqueServices = [...new Set(servicesData?.map(item => item.api_service) || [])];

      // Få siste log-tid
      const { data: lastLogData } = await supabase
        .from('api_call_logs')
        .select('created_at')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      setStats({
        totalLogs: totalCount || 0,
        logsToday: todayCount || 0,
        uniqueServices,
        lastLogTime: lastLogData?.created_at || null
      });
    } catch (error) {
      console.error('Error fetching logging stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Logging-status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Akkurat nå';
    if (diffInMinutes < 60) return `${diffInMinutes}m siden`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}t siden`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d siden`;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Logging-status
          </CardTitle>
          <CardDescription>
            Oversikt over API-logging aktivitet
          </CardDescription>
        </div>
        {stats && stats.totalLogs > 0 ? (
          <CheckCircle className="h-6 w-6 text-green-600" />
        ) : (
          <AlertCircle className="h-6 w-6 text-orange-600" />
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {stats?.totalLogs || 0}
            </div>
            <p className="text-sm text-muted-foreground">Totalt antall logger</p>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">
              {stats?.logsToday || 0}
            </div>
            <p className="text-sm text-muted-foreground">Logger i dag</p>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium mb-2">Aktive tjenester:</p>
          <div className="flex flex-wrap gap-2">
            {stats?.uniqueServices.map((service) => (
              <Badge key={service} variant="secondary">
                {service === 'enhetsregisteret' ? 'Enhetsregisteret' :
                 service === 'arbeidsplassen' ? 'Arbeidsplassen' :
                 service === 'utdanning' ? 'Utdanning.no' : service}
              </Badge>
            )) || <Badge variant="outline">Ingen aktivitet</Badge>}
          </div>
        </div>

        {stats?.lastLogTime && (
          <div>
            <p className="text-sm text-muted-foreground">
              Siste aktivitet: {formatTimeAgo(stats.lastLogTime)}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
